<i18n lang="yaml" locale="en">
  name: Name
  click behaviour: Click behaviour
  fullscreen: Fullscreen
  ignore: Ignore
  PDF settings: PDF settings
  expand pages: Expand pages
  select pages: Select pages
  page from: from Page
  page to: to Page
  caption: Caption
</i18n>

<i18n lang="yaml" locale="de">
  name: Name
  click behaviour: Klickverhalten
  fullscreen: Fullscreen
  ignore: Ignore
  PDF settings: PDF-Einstellungen
  expand pages: Alle Seiten anzeigen
  select pages: Seiten auswählen
  page from: ab Seite
  page to: bis Seite
  caption: Beschriftung
</i18n>

<template>
  <div
    @click.capture="handleFullscreen"
    :class="{
      'asset-browser-item': !isInProject && !isInPropEditor
    }"
    class="pdf"
  >
    <div
      ref="container"
      class="content"
    />
    <div
      v-if="isFullscreen && caption"
      class="caption"
    >
      {{ caption }}
    </div>
    <div
      v-if="!isInProject || isInPropEditor"
      class="name"
    >
      {{ name }}
    </div>
    <keep-alive>
      <FileEditor
        v-if="$editMode && !isInPropEditor"
        ref="editor"
      />
    </keep-alive>
  </div>
</template>

<script>
import FileEditor from '@/components/editor/FileEditor'
import pdfjsLib from 'pdfjs-dist/webpack'
import { PDFPageView } from 'pdfjs-dist/web/pdf_viewer'
import Vue from 'vue'
import BaseModule from '@/components/modules/BaseModule'
import translatable from '@/store/mixins/translatable'

export default {
  name: 'PdfViewer',
  extends: BaseModule,
  customStore: translatable,
  components: {
    FileEditor
  },
  data () {
    return {
      pdfViewer: null
    }
  },
  inject: ['$cache'],
  props: {
    isInPropEditor: {
      type: Boolean,
      default: false
    },
    isFullscreen: {
      type: Boolean,
      default: false
    },
    src: {
      type: String,
      required: true
    },
    ratio: {
      type: Number,
      default: undefined,
      min: 0
    },
    name: {
      type: String,
      default: ''
    },
    page: {
      type: Number,
      default: 1
    },
    scale: {
      type: String,
      default: 'width'
    },
    clickBehaviour: {
      type: String,
      default: 'fullscreen',
      validator (value) {
        return ['fullscreen', 'none'].includes(value)
      }
    },
    caption: {
      type: String,
      default: ''
    }
  },
  computed: {
    isInProject () {
      return !this.$store.getters.path.startsWith('/assets/')
    },
    document () {
      if (!this.$cache[this.src]) {
        Vue.set(this.$cache, this.src, {
          document: pdfjsLib.getDocument(this.src).promise,
          pages: []
        })
        // eslint-disable-next-line vue/no-async-in-computed-properties
        this.$cache[this.src].document.then(doc => {
          Vue.set(this.$cache[this.src], 'numPages', doc.numPages)
        })
      }
      return this.$cache[this.src].document
    },
    pages () {
      return this.$cache[this.src] && this.$cache[this.src].pages
    }
  },
  watch: {
    async page (newPage) {
      if (this.pages) {
        const document = await this.document
        if (!this.pages[newPage]) {
          Vue.set(this.pages, newPage, {
            data: document.getPage(newPage)
          })
        }
        const page = await this.pages[newPage].data
        this.pdfViewer.setPdfPage(page)
        if (!this.pages[newPage].ratio) {
          Vue.set(this.pages[newPage], 'ratio', this.pdfViewer.viewport.width / this.pdfViewer.viewport.height)
        }
        this.draw()
      }
    }
  },
  methods: {
    handleFullscreen () {
      if (this.isInProject && !this.$editMode && !this.isFullscreen && this.clickBehaviour === 'fullscreen') {
        this.$router.push({ path: this.$store.getters.path + '-' + this.page, query: { fullscreen: true } })
      }
    },
    draw () {
      if (this.pdfViewer) {
        this.pdfViewer.update(1)
        if (this.scale === 'width') {
          this.pdfViewer.update(this.$refs.container.offsetWidth / this.pdfViewer.viewport.width)
        } else {
          this.pdfViewer.update(this.$refs.container.offsetHeight / this.pdfViewer.viewport.height)
        }
        this.pdfViewer.draw()
      }
    }
  },
  updated () {
    if (this.pdfViewer && this.$refs.container) {
      this.draw()
    }
  },
  async mounted () {
    const document = await this.document

    if (!this.pages[this.page]) {
      Vue.set(this.pages, this.page, {
        data: document.getPage(this.page)
      })
    }
    const page = await this.pages[this.page].data

    // since this hook is async, the component might already be destroyed when we are here
    // in this case the container ref is not set anymore and we can cancel the remaining code
    if (this.$refs.container) {
      if (!this.pdfViewer) {
        // create pdfViewer
        this.pdfViewer = new PDFPageView({
          container: this.$refs.container,
          removePageBorders: true,
          textLayerMode: 0,
          id: [...this.$store.getters._path, this.page].join('_'),
          scale: 1,
          defaultViewport: page.getViewport({ scale: 1 })
        })
      }

      this.pdfViewer.setPdfPage(page)
      if (!this.pages[this.page].ratio) {
        Vue.set(this.pages[this.page], 'ratio', this.pdfViewer.viewport.width / this.pdfViewer.viewport.height)
      }
      this.draw()
    }
  },
  beforeDestroy () {
    if (this.pdfViewer) {
      this.pdfViewer.destroy()
    }
  }
}
</script>

<style lang="css" src="pdfjs-dist/web/pdf_viewer.css" />

<style lang="scss" scoped>
.content {
  pointer-events: none;
}

.pdf:not(.fullscreen) {
  position: relative;

  .content {
    width: 100%;
    height: 100%;
  }
}

.pdf.fullscreen {
  .content {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
